import { AfterViewInit, Component, computed, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { IonNav } from '@ionic/angular/standalone';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { FullPageLayoutComponent } from '@app/core/layout/full-page-layout/full-page-layout.component';
import { SharedModule } from '@app/shared/shared.module';
import { ButtonComponent } from '@app/shared/ui/button/button.component';
import { ModalController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { DynamicFlowSandbox } from './sandboxes/dynamic-flow.sandbox';
import { DynamicFlowStepComponent } from './pages/dynamic-flow-step/dynamic-flow-step.component';
import { BrandedContentStepComponent } from './components/branded-content-step/branded-content-step.component';
import { DynamicFlowStepType } from './model/dynamic-flow.types';

export const DYNAMIC_FLOW_ROLE_CANCEL = 'cancel';
export const DYNAMIC_FLOW_ROLE_COMPLETE = 'complete';
export const DYNAMIC_FLOW_ROLE_START_BOOKING = 'start-booking';
export const DYNAMIC_FLOW_ROLE_NEXT_FLOW = 'next-flow';

@Component({
  selector: 'c-dynamic-flow',
  imports: [FullPageLayoutComponent, SharedModule, TranslocoPipe, BrandedContentStepComponent, ButtonComponent],
  templateUrl: './dynamic-flow.component.html',
  styleUrl: './dynamic-flow.component.scss',
})
export class DynamicFlowComponent implements AfterViewInit, OnDestroy {
  @Input() flowId: string;
  @Input() externalId?: string;

  @ViewChild('ionNav') ionNav: IonNav;

  private readonly modalCtrl = inject(ModalController);
  private readonly platform = inject(Platform);
  private readonly dynamicFlowSandbox = inject(DynamicFlowSandbox);
  private backButtonSubscription: Subscription;

  readonly currentStep = this.dynamicFlowSandbox.currentStep;
  readonly loadingFlow = this.dynamicFlowSandbox.loadingFlow;
  readonly loadingNext = this.dynamicFlowSandbox.loadingNext;
  readonly loadingPrevious = this.dynamicFlowSandbox.loadingPrevious;

  showInitialSkeleton = computed(() => this.loadingFlow() && !this.currentStep());
  isBrandedContentStep = computed(() => this.currentStep()?.step?.type === DynamicFlowStepType.BRANDED_CONTENT);
  stepTitle = computed(() => this.currentStep()?.step.title);
  progressBarValue = computed(
    () => this.currentStep() ? this.currentStep().navigation.currentStep / this.currentStep().navigation.estimatedSteps : 0
  );

  readonly layoutConfig = {
    header: { fullWidth: false },
    footer: { showBorder: false },
  };

  readonly closeIcon = faXmark;

  ngOnInit(): void {
    this.startFlow();
  }

  ngAfterViewInit(): void {
    this.setNavRoot();
    this.subscribeToHardwareBackButton();
  }

  ngOnDestroy(): void {
    this.backButtonSubscription?.unsubscribe();
    this.dynamicFlowSandbox.cleanup();
  }

  next(): void {
    this.dynamicFlowSandbox.next(this.flowId).subscribe(() => this.ionNav.push(DynamicFlowStepComponent));
  }

  previous(): void {
    this.dynamicFlowSandbox.previous(this.flowId).subscribe(() => this.ionNav.pop());
  }
  
  onBrandedAction(): void {
    if (this.currentStep().navigation.isLastStep) {
      this.complete();
      return;
    }
    this.next();
  }

  complete(): void {
    if (this.currentStep().navigation.submitOnLastStep) {
      this.dynamicFlowSandbox.next(this.flowId).subscribe(() => this.finishComplete());
      return;
    }
    this.finishComplete();
  }

  closeModal(role: typeof DYNAMIC_FLOW_ROLE_CANCEL | typeof DYNAMIC_FLOW_ROLE_COMPLETE): void {
    if (this.currentStep()?.navigation.isLastStep && this.currentStep()?.step.type === DynamicFlowStepType.CONTENT) {
      this.dynamicFlowSandbox.logDynamicFlowComplete(this.flowId);
      this.modalCtrl.dismiss(null, DYNAMIC_FLOW_ROLE_COMPLETE);
      return;
    }
    this.modalCtrl.dismiss(null, role);
  }

  closeModalAndStartBooking(): void {
    this.modalCtrl.dismiss(this.currentStep().navigation.booking?.type, DYNAMIC_FLOW_ROLE_START_BOOKING);
  }

  closeModalAndStartNextFlow(): void {
    this.modalCtrl.dismiss(this.currentStep().navigation.nextFlow, DYNAMIC_FLOW_ROLE_NEXT_FLOW);
  }

  private finishComplete(): void {
    this.dynamicFlowSandbox.logDynamicFlowComplete(this.flowId);
    if (this.currentStep().navigation.nextFlow) {
      this.closeModalAndStartNextFlow();
    } else if (this.currentStep().navigation.canStartBooking) {
      this.closeModalAndStartBooking();
    } else {
      this.closeModal(DYNAMIC_FLOW_ROLE_COMPLETE);
    }
  }

  private startFlow(): void {
    this.dynamicFlowSandbox.startFlow(this.flowId, this.externalId);
  }

  private setNavRoot(): void {
    this.ionNav.setRoot(DynamicFlowStepComponent);
  }

  private subscribeToHardwareBackButton(): void {
    this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(101, () => {
      const navigation = this.currentStep()?.navigation;
      const showPrevious = navigation?.buttons?.showPrevious;
      const isFirstStep = navigation?.isFirstStep;

      if (!showPrevious && !isFirstStep) {
        return;
      }

      this.ionNav.canGoBack().then((canGoBack) => {
        if (canGoBack) {
          this.previous();
        } else {
          this.closeModal(DYNAMIC_FLOW_ROLE_CANCEL);
        }
      });
    });
  }
}
